import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import client from "@/lib/api";
import { rarityMeta } from "@/lib/itemMeta";
import { imageUrl } from "@/pages/Items";
import ItemFrame from "@/components/ItemFrame";
import { Dice5, Sparkles, Box, ChevronDown } from "lucide-react";

/**
 * Renders for Case-type items: weighted drop grid + "open the case" roll simulator.
 * Props: item — the Case item, must have `drops: [{slug, weight}]`.
 */
export function CaseDropsPanel({ item }) {
  const drops = Array.isArray(item.drops) ? item.drops : [];
  const [resolved, setResolved] = useState(null); // map slug -> item
  const [loading, setLoading] = useState(true);
  const [rolling, setRolling] = useState(false);
  const [rolledSlug, setRolledSlug] = useState(null);
  const [history, setHistory] = useState([]); // recent slugs
  const [expanded, setExpanded] = useState(false); // drop pool collapsed by default

  useEffect(() => {
    if (drops.length === 0) { setResolved({}); setLoading(false); return; }
    let cancelled = false;
    (async () => {
      const map = {};
      await Promise.all(drops.map(async d => {
        try {
          const r = await client.get(`/items/${d.slug}`);
          map[d.slug] = r.data;
        } catch (_) {
          map[d.slug] = null; // unknown slug
        }
      }));
      if (!cancelled) { setResolved(map); setLoading(false); }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [item.slug]);

  const totalWeight = useMemo(
    () => drops.reduce((s, d) => s + Math.max(0, Number(d.weight) || 0), 0) || 1,
    [drops]
  );

  // Sort highest-weight first for display.
  const sorted = useMemo(
    () => [...drops]
      .map(d => ({ ...d, pct: (Math.max(0, Number(d.weight) || 0) / totalWeight) * 100 }))
      .sort((a, b) => b.pct - a.pct),
    [drops, totalWeight]
  );

  if (drops.length === 0) return null;

  const pickWeighted = () => {
    const r = Math.random() * totalWeight;
    let acc = 0;
    for (const d of drops) {
      acc += Math.max(0, Number(d.weight) || 0);
      if (r <= acc) return d.slug;
    }
    return drops[drops.length - 1].slug;
  };

  const handleOpen = () => {
    if (rolling) return;
    setRolling(true);
    setRolledSlug(null);

    // Quick "spin": cycle through slugs for ~1.4s before landing.
    const slugList = sorted.map(d => d.slug);
    const ticks = 18;
    const final = pickWeighted();
    let i = 0;
    const interval = setInterval(() => {
      setRolledSlug(slugList[i % slugList.length]);
      i++;
      if (i >= ticks) {
        clearInterval(interval);
        setRolledSlug(final);
        setHistory(h => [final, ...h].slice(0, 5));
        setRolling(false);
      }
    }, 70);
  };

  const rolledItem = rolledSlug && resolved ? resolved[rolledSlug] : null;
  const rolledMeta = rolledItem ? rarityMeta(rolledItem.rarity) : null;

  return (
    <div className="space-y-4 mb-6" data-testid="case-drops-panel">
      {/* Open-the-case simulator */}
      <div className="panel corner-frame relative overflow-hidden p-4 md:p-6">
        <div className="absolute -right-24 -top-24 w-72 h-72 rounded-full bg-[radial-gradient(circle,_rgba(102,230,255,0.18)_0%,_transparent_70%)] pointer-events-none" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[var(--t-cyan)] mb-1">
              // Open Simulator
            </div>
            <h3 className="font-display text-lg md:text-xl text-[var(--t-text)] uppercase leading-tight">
              Crack One Open
            </h3>
            <p className="text-xs md:text-sm text-[var(--t-text-dim)] mt-1 max-w-md">
              Preview-only — rolls a single item using the weighted drop pool below.
            </p>
          </div>

          {/* Reveal slot */}
          <div className="flex items-center gap-4">
            <div
              className={`w-24 h-24 md:w-28 md:h-28 border-2 ${rolling ? "border-[var(--t-blue)]" : "border-[var(--t-border)]"} relative overflow-hidden flex items-center justify-center bg-[var(--t-bg-2)]`}
              style={{
                boxShadow: rolledMeta && !rolling
                  ? `0 0 28px ${rolledMeta.glow}, inset 0 0 18px ${rolledMeta.glow}`
                  : rolling ? "0 0 22px var(--t-blue-glow)" : "none",
              }}
              data-testid="case-roll-slot"
            >
              {rolledItem ? (
                <img
                  src={imageUrl(rolledItem.image_url) || ""}
                  alt={rolledItem.name}
                  className={`w-full h-full object-contain transition-all ${rolling ? "blur-[1px] scale-95" : ""}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              ) : (
                <Box className="w-10 h-10 text-[var(--t-text-muted)]" />
              )}
              {rolledItem && !rolling && (
                <span
                  className="absolute bottom-0 inset-x-0 text-center font-mono text-[9px] uppercase tracking-[0.18em] py-1 backdrop-blur-sm"
                  style={{ background: "rgba(5,9,18,0.85)", color: rolledMeta.color }}
                >
                  {rolledItem.rarity}
                </span>
              )}
            </div>
            <button
              onClick={handleOpen}
              disabled={rolling || loading}
              className="btn-primary"
              data-testid="case-open-btn"
            >
              {rolling ? (
                <>
                  <Sparkles className="w-4 h-4 animate-pulse" /> Rolling
                </>
              ) : (
                <>
                  <Dice5 className="w-4 h-4" /> Open Case
                </>
              )}
            </button>
          </div>
        </div>

        {rolledItem && !rolling && (
          <div className="relative mt-4 pt-4 border-t border-dashed border-[var(--t-border)] flex flex-wrap items-center justify-between gap-3" data-testid="case-roll-result">
            <div className="min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--t-text-muted)] mb-1">
                Result
              </div>
              <Link
                to={`/wiki/items/${rolledItem.slug}`}
                className="font-display text-lg md:text-xl uppercase tracking-[0.04em] hover:underline"
                style={{ color: rolledMeta.color, textShadow: `0 0 18px ${rolledMeta.glow}` }}
              >
                {rolledItem.name}
              </Link>
            </div>
            {history.length > 0 && (
              <div className="text-right">
                <div className="font-mono text-[9px] uppercase tracking-[0.22em] text-[var(--t-text-muted)] mb-1">
                  Last {history.length} roll{history.length > 1 ? "s" : ""}
                </div>
                <div className="flex gap-1.5 justify-end">
                  {history.map((s, i) => {
                    const it = resolved?.[s];
                    const meta = it ? rarityMeta(it.rarity) : null;
                    return (
                      <span
                        key={i}
                        className="w-2 h-2 rounded-full"
                        style={{
                          background: meta?.color || "var(--t-text-muted)",
                          boxShadow: meta ? `0 0 6px ${meta.glow}` : "none",
                        }}
                        title={it?.name || s}
                      />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Drop pool grid (collapsed by default to keep page short) */}
      <div className="panel p-5" data-testid="case-drops-grid">
        <button
          type="button"
          onClick={() => setExpanded(v => !v)}
          className="w-full flex items-end justify-between text-left group"
          aria-expanded={expanded}
          data-testid="case-drops-toggle"
        >
          <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase group-hover:text-[var(--t-cyan)] transition">
            // Drop Pool · {drops.length} item{drops.length !== 1 ? "s" : ""}
          </div>
          <div className="flex items-center gap-3">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--t-text-muted)]">
              Σ weight {Math.round(totalWeight)}
            </span>
            <ChevronDown
              className={`w-4 h-4 text-[var(--t-text-dim)] group-hover:text-[var(--t-cyan)] transition-transform ${expanded ? "rotate-180" : ""}`}
            />
          </div>
        </button>

        {expanded && (
          loading ? (
            <div className="font-mono text-xs tracking-[0.3em] text-[var(--t-blue-glow)] uppercase pulse-glow text-center py-6 mt-4">
              // Loading drops...
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-4">
              {sorted.map(d => {
                const it = resolved?.[d.slug];
                const meta = it ? rarityMeta(it.rarity) : null;
                return (
                  <Link
                    key={d.slug}
                    to={`/wiki/items/${d.slug}`}
                    className="group block panel relative overflow-hidden hover:border-[var(--t-blue)] transition"
                    style={{
                      borderColor: meta?.color,
                      boxShadow: meta ? `0 0 10px ${meta.glow}` : undefined,
                    }}
                    data-testid={`drop-card-${d.slug}`}
                  >
                    <div className="aspect-square">
                      <ItemFrame
                        rarity={it?.rarity || "Common"}
                        image={imageUrl(it?.image_url)}
                        alt={it?.name || d.slug}
                        className="w-full h-full"
                      />
                    </div>
                    <div className="p-2.5 border-t border-[var(--t-border)] bg-[var(--t-bg)]/60">
                      <div
                        className="font-display text-sm uppercase tracking-[0.04em] truncate leading-tight"
                        style={{ color: meta?.color || "var(--t-text)" }}
                      >
                        {it?.name || d.slug}
                      </div>
                      <div className="flex items-center justify-between mt-1">
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--t-text-muted)]">
                          {it?.rarity || "—"}
                        </span>
                        <span className="font-mono text-[11px] font-semibold" style={{ color: meta?.color || "var(--t-cyan)" }}>
                          {d.pct.toFixed(1)}%
                        </span>
                      </div>
                      <div className="mt-1.5 h-0.5 bg-[var(--t-panel-2)] overflow-hidden">
                        <div
                          className="h-full"
                          style={{
                            width: `${d.pct}%`,
                            background: meta?.color || "var(--t-cyan)",
                            boxShadow: `0 0 6px ${meta?.glow || "var(--t-cyan)"}`,
                          }}
                        />
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )
        )}
      </div>
    </div>
  );
}

/**
 * Renders "Drops From" panel on a non-case item's detail page, listing every
 * case that includes this slug in its drop pool.
 */
export function DropsFromPanel({ slug }) {
  const [sources, setSources] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await client.get(`/items/${slug}/drop-sources`);
        if (!cancelled) setSources(r.data || []);
      } catch (_) {
        if (!cancelled) setSources([]);
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="panel p-5 mb-6" data-testid="drops-from-panel">
      <div className="font-mono text-xs tracking-[0.25em] text-[var(--t-blue-glow)] uppercase mb-3">
        // Drops From
      </div>
      <div className="flex flex-wrap gap-2">
        {sources.map(s => {
          const meta = rarityMeta(s.rarity);
          return (
            <Link
              key={s.slug}
              to={`/wiki/items/${s.slug}`}
              className="inline-flex items-center gap-2 px-3 py-1.5 border bg-[var(--t-bg-2)]/60 hover:bg-[var(--t-bg-2)] transition group"
              style={{ borderColor: meta.color, boxShadow: `0 0 8px ${meta.glow}` }}
              data-testid={`drops-from-${s.slug}`}
            >
              <span
                className="font-display text-sm uppercase tracking-[0.04em]"
                style={{ color: meta.color }}
              >
                {s.name}
              </span>
              <span className="font-mono text-[11px] text-[var(--t-cyan)]">
                {s.drop_pct.toFixed(1)}%
              </span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
